import { tenantService } from '@services/domain-factories/tenant';
import { initOnce } from '@utils/zustand/initOnce';
import { Config } from '@vincerosoft/assessfluence-types';
import { createStoreWithSelector } from '../../utils/zustand/createStoreWithSelector';
import { createUseStore } from '../../utils/zustand/createUseStore';
import { WithReset } from '../../utils/zustand/zustandTypes';

type State = {
  tenantAccount: Config['account'] | null;
  appSettings: Config['app_settings'] | null;
};

type Actions = {
  initTenantConfig: () => Promise<void>;
  handleAdminAccountChange: (config: Partial<Config['account']>) => void;
  handleAppSettingsChange: (config: Partial<Config['app_settings']>) => void;
  reset: () => void;
};

const baseState = {
  isInitialized: false,
  loading: false,
  inFlightInit: null as unknown as Promise<void> | null,
  // Bumped by reset() so in-flight async writes from a prior session can detect staleness and no-op.
  resetToken: 0
};

const initialData: State = {
  tenantAccount: null,
  appSettings: null
};

export const appTenantState = createStoreWithSelector<
  WithReset<State & typeof baseState & Actions>
>()((set, get) => ({
  ...initialData,
  ...baseState,
  handleAdminAccountChange: (config: Partial<Config['account']>) => {
    const adminAccount = get().tenantAccount;

    set({
      tenantAccount: {
        ...(adminAccount ? adminAccount : {}),
        ...config
      } as Config['account']
    });
  },
  handleAppSettingsChange: (config: Partial<Config['app_settings']>) => {
    set({
      appSettings: {
        ...get().appSettings,
        ...config
      } as Config['app_settings']
    });
  },
  reset: () => set((state) => ({ ...initialData, ...baseState, resetToken: state.resetToken + 1 })),
  initTenantConfig: () =>
    initOnce(
      'isInitialized',
      set,
      get,
      () => tenantService.getTenantConfig(),
      (config) => {
        return set({
          tenantAccount: config.adminAccount,
          appSettings: config.appSettings,
          isInitialized: true
        });
      }
    )
}));

export const useAppTenantState = createUseStore(appTenantState);
